import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { API } from '../utils/constants';

function GiftCardLookup() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [giftCard, setGiftCard] = useState(null);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Vinsamlegast sláðu inn gjafakortakóða');
      return;
    }

    setLoading(true);
    setError('');
    setGiftCard(null);

    try {
      const response = await axios.get(`${API}/public/giftcards/check/${code.trim()}`);
      setGiftCard(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Gjafakort fannst ekki. Vinsamlegast athugaðu kóðann.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Villa kom upp við að fletta upp gjafakorti');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('is-IS').format(amount) + ' kr.';
  };

  const getStatusBadge = () => {
    if (!giftCard) return null;

    if (giftCard.expired) {
      return <Badge className="bg-red-600 text-white">Útrunnið</Badge>;
    }
    if (giftCard.status === 'fully_used') {
      return <Badge className="bg-gray-600 text-white">Notað</Badge>;
    }
    if (giftCard.status === 'partially_used') {
      return <Badge className="bg-yellow-600 text-white">Að hluta notað</Badge>;
    }
    if (giftCard.status === 'active') {
      return <Badge className="bg-green-600 text-white">Virkt</Badge>;
    }
    return <Badge className="bg-gray-600 text-white">{giftCard.status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎁</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">
            Gjafakortauppfletting
          </h1>
          <p className="text-base text-slate-600">
            Athugaðu stöðu og eftirstöðvar gjafakorts þíns
          </p>
        </div>

        {/* Search Card */}
        <Card className="shadow-xl border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-xl text-slate-900">Sláðu inn gjafakortakóða</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="T.d. ABC123XYZ"
                  className="text-center text-lg font-mono tracking-wider h-12"
                  disabled={loading}
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Gjafakortakóðinn er á gjafabréfinu þínu
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm text-center">❌ {error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Fletti upp...
                  </>
                ) : (
                  <>
                    🔍 Fletta upp gjafakorti
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results Card */}
        {giftCard && (
          <Card className="shadow-xl border-slate-200 animate-in fade-in duration-500">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-slate-900">
                  Gjafakort {code}
                </CardTitle>
                {getStatusBadge()}
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Main Balance Display */}
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <p className="text-sm text-slate-600 mb-2">Eftirstöðvar</p>
                <p className="text-5xl font-bold text-green-700">
                  {formatCurrency(giftCard.remaining_balance)}
                </p>
              </div>

              {/* Gift Card Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-600 mb-1">Upphafsupphæð</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {formatCurrency(giftCard.original_amount)}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-600 mb-1">Notuð upphæð</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {formatCurrency(giftCard.original_amount - giftCard.remaining_balance)}
                  </p>
                </div>
              </div>

              {/* Expiration Date */}
              {giftCard.expiration_date && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-900">
                        {giftCard.expired ? '⏰ Gjafakort útrunnið' : '📅 Gildistími'}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        {new Date(giftCard.expiration_date).toLocaleDateString('is-IS', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    {giftCard.expired && (
                      <Badge className="bg-red-600 text-white">Útrunnið</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Gift Card Type Info */}
              {giftCard.giftcard_type === 'service' && giftCard.service_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-1">🚗 Þjónusta</p>
                  <p className="text-base text-blue-800">{giftCard.service_name}</p>
                  {giftCard.valid_for_size_label && (
                    <p className="text-xs text-blue-600 mt-1">
                      Gilt fyrir: {giftCard.valid_for_size_label}
                    </p>
                  )}
                  {giftCard.service_redeemed && (
                    <Badge className="mt-2 bg-green-600 text-white">✅ Notað</Badge>
                  )}
                </div>
              )}

              {giftCard.giftcard_type === 'package' && giftCard.services && giftCard.services.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-purple-900 mb-2">📦 Pakki</p>
                  <ul className="space-y-1">
                    {giftCard.services.map((service, idx) => (
                      <li key={idx} className="text-sm text-purple-800 flex items-center gap-2">
                        {giftCard.services_redeemed?.includes(service) ? (
                          <span className="text-green-600">✅</span>
                        ) : (
                          <span className="text-slate-400">⭕</span>
                        )}
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Test Mode Warning */}
              {giftCard.is_test_mode && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 text-center">
                    ⚠️ Þetta er prófunargjafakort
                  </p>
                </div>
              )}

              {/* Search Again Button */}
              <Button
                onClick={() => {
                  setCode('');
                  setGiftCard(null);
                  setError('');
                }}
                variant="outline"
                className="w-full h-10"
              >
                🔄 Fletta upp öðru gjafakorti
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>
            Ef þú átt í vandræðum með gjafakortið þitt, vinsamlegast hafðu samband við{' '}
            <a href="mailto:info@alvorubon.is" className="text-blue-600 hover:underline">
              info@alvorubon.is
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default GiftCardLookup;
